import { Button } from "@groot/ui/button";
import { PageHeader } from "@groot/shell/components/layout";
import { Page } from "@/app/shared/components/page";
import { Input } from "@groot/ui/input";
import { LoadingSpinner } from "@groot/ui/loading-spinner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@groot/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@groot/ui/table";
import { useConfirm } from "@groot/ui/primitives";
import { toast } from "@/lib/toast";
import { formatDisplayDateTime } from "@/lib/utils";
import { apiClient } from "@/services/api";
import type { TransactionalSms as TransactionalSmsType } from "@/types";
import { AlertCircle, CheckCircle, XCircle as ClearIcon, Search, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

export function TransactionalSms() {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [smsList, setSmsList] = useState<TransactionalSmsType[]>([]);
  const [senders, setSenders] = useState<string[]>([]);
  const [selectedSender, setSelectedSender] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);

  const pageSize = 50;

  useEffect(() => {
    loadSenders();
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: loadData is not stable
  useEffect(() => {
    if (page !== 0) {
      setPage(0);
    } else {
      loadData();
    }
  }, [selectedSender, searchQuery]);

  useEffect(() => {
    if (page >= 0) {
      loadData();
    }
  }, [page]);

  const loadData = async () => {
    try {
      setLoading(true);

      const filters: {
        from?: string;
        search?: string;
        limit: number;
        offset: number;
      } = {
        limit: pageSize,
        offset: page * pageSize,
      };

      if (selectedSender !== "all") {
        filters.from = selectedSender;
      }

      if (searchQuery.trim()) {
        filters.search = searchQuery.trim();
      }

      const { smsList: smsData, total: totalCount } = await apiClient.getTransactionalSms(filters);

      setSmsList(smsData);
      setTotal(totalCount);
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to load transactional SMS",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadSenders = async () => {
    try {
      const sendersData = await apiClient.getTransactionalSmsSenders();
      setSenders(sendersData);
    } catch (error) {
      console.error("Failed to load senders:", error);
    }
  };

  const handleDelete = async (id: number) => {
    if (
      !(await confirm({
        title: "Confirm Action",
        description: "Are you sure you want to delete this SMS?",
      }))
    ) {
      return;
    }

    try {
      await apiClient.deleteTransactionalSms(id);
      toast({
        title: "Success",
        description: "SMS deleted successfully",
      });
      loadData();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to delete SMS",
      });
    }
  };

  const handleClearFilters = () => {
    setSelectedSender("all");
    setSearchQuery("");
  };

  const hasActiveFilters = selectedSender !== "all" || searchQuery.trim() !== "";

  const getStatus = (sms: TransactionalSmsType) => {
    if (sms.Transaction && sms.Transaction.length > 0) {
      return {
        text: `${sms.Transaction.length} txn`,
        color: "text-success",
        icon: <CheckCircle className="size-3 mr-1 inline" />,
      };
    }
    if (sms.extractedOutput) {
      return {
        text: "Parsed",
        color: "text-muted-foreground",
        icon: <AlertCircle className="size-3 mr-1 inline" />,
      };
    }
    return {
      text: "Not parsed",
      color: "text-muted-foreground/70",
      icon: null,
    };
  };

  return (
    <Page>
      {/* Header */}
      <PageHeader
        title="Transactional SMS"
        description={`${total} ${total === 1 ? "message" : "messages"}`}
        className="mb-8"
      />

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground/70" />
          <Input
            placeholder="Search SMS..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 border-input"
          />
        </div>

        <Select value={selectedSender} onValueChange={setSelectedSender}>
          <SelectTrigger className="w-full md:w-[200px] border-input">
            <SelectValue placeholder="All Senders" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Senders</SelectItem>
            {senders.map((sender) => (
              <SelectItem key={sender} value={sender}>
                {sender}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {hasActiveFilters && (
          <Button variant="outline" onClick={handleClearFilters} className="gap-2">
            <ClearIcon className="size-4" />
            Clear
          </Button>
        )}
      </div>

      {/* SMS Table */}
      {loading ? (
        <LoadingSpinner size="lg" className="py-20" />
      ) : smsList.length === 0 ? (
        <div className="text-center py-20">
          <p className="text-muted-foreground">
            {hasActiveFilters ? "No SMS messages found" : "No SMS messages yet"}
          </p>
        </div>
      ) : (
        <>
          <div className="border border-border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted hover:bg-muted">
                  <TableHead className="font-medium text-foreground/80 w-[80px]">ID</TableHead>
                  <TableHead className="font-medium text-foreground/80">Date</TableHead>
                  <TableHead className="font-medium text-foreground/80">From</TableHead>
                  <TableHead className="font-medium text-foreground/80">Message</TableHead>
                  <TableHead className="font-medium text-foreground/80">Status</TableHead>
                  <TableHead className="w-[60px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {smsList.map((sms) => {
                  const status = getStatus(sms);
                  return (
                    <TableRow
                      key={sms.id}
                      className="cursor-pointer hover:bg-muted border-border"
                      onClick={() => navigate(`/transactional-sms/${sms.id}`)}
                    >
                      <TableCell className="font-mono text-[0.9em] text-muted-foreground">
                        {sms.id}
                      </TableCell>
                      <TableCell className="text-sm text-foreground whitespace-nowrap">
                        {formatDisplayDateTime(sms.date)}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {sms.from || "Unknown"}
                      </TableCell>
                      <TableCell className="text-sm text-foreground max-w-md truncate">
                        {sms.body || "-"}
                      </TableCell>
                      <TableCell className={`text-sm ${status.color}`}>
                        {status.icon}
                        {status.text}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="size-8 p-0 text-muted-foreground/70 hover:text-muted-foreground"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(sms.id);
                          }}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between mt-4">
            <div className="text-sm text-muted-foreground">
              Showing {page * pageSize + 1} to {Math.min((page + 1) * pageSize, total)} of {total}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(page - 1)}
                disabled={page === 0}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(page + 1)}
                disabled={(page + 1) * pageSize >= total}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </Page>
  );
}
